﻿function WishlistWidgetFactory(container) {
  class WishlistWidget {
    constructor(container) {
      this.wishlistWidget = container;
      this.isBuilding = false;
      this.loadingIndicator = null;
      this.hiddenChildren = [];
      this.currentWishlistName = window.localStorage?.getItem('current_wishlist_name') || null;
      this.expectingDetailView = false;
      this.isDetailViewActive = false;
      this._init();
    }

    appendHtml(selector, htmlContent, position = "beforeend") {
      const div = selector;
      if (div) {
        div.insertAdjacentHTML(position, htmlContent);
      } else {
        console.warn(`Element with selector "${selector}" not found.`);
      }
    }

    _init() {
      this._showLoadingIndicator();
      const observer = new MutationObserver(() => {
        const anyListView = this.wishlistWidget.querySelector('.swym-wishlist-list, .swym-empty-wishlist-container');
        if (anyListView && !anyListView.hasAttribute('data-custom-setup')) {
          anyListView.setAttribute('data-custom-setup', 'true');
          this._onListViewReady();
        }
        const detailView = this.wishlistWidget.querySelector('.swym-wishlist-detail');
        if (detailView && !detailView.hasAttribute('data-custom-setup')) {
          detailView.setAttribute('data-custom-setup', 'true');
          this._onDetailViewReady();
        }
      });

      observer.observe(this.wishlistWidget, {
        childList: true,
        subtree: true,
      });
    }

    _onListViewReady() {
      this._hideLoadingIndicator();
      const emptyContainer = this.wishlistWidget.querySelector('.swym-empty-wishlist-container');
      const detailView = this.wishlistWidget.querySelector('.swym-wishlist-detail');
      const shouldStayInDetail = this.expectingDetailView || this.isDetailViewActive || !!detailView;
      if (emptyContainer && shouldStayInDetail) {
        this._renderCustomElements("empty_detail");
        this.isDetailViewActive = true;
      } else if (emptyContainer) {
        this._renderCustomElements("empty_list");
        this.isDetailViewActive = false;
      } else {
        this._renderCustomElements("general_header");
        this._setupWishlistCardInterception();
        this._startProductCountObserver();
        this.isDetailViewActive = !!detailView;
      }
      this.expectingDetailView = false;
    }

    _startProductCountObserver() {
      const updateProductCount = () => {
        document.querySelectorAll(".swym-wishlist-product-count").forEach(productCount => {
           const text = productCount.textContent || "";
           const countMatch = text.match(/(\d+)/);

           if (countMatch) {
             const count = parseInt(countMatch[0], 10);
             const template = count === 1
                ? window.themeVariables.translations.wishlist.productCount
                : (window.themeVariables.translations.wishlist.productCountPlural || window.themeVariables.translations.wishlist.productCount + "s");

             const newText = template.replace('{{count}}', count);

             if (productCount.textContent !== newText) {
                productCount.textContent = newText;
             }
           }
        });
      };

      updateProductCount();
      new MutationObserver(updateProductCount).observe(this.wishlistWidget, { childList: true, subtree: true, characterData: true });
    }

    _onDetailViewReady() {
      this._hideLoadingIndicator();
      this._renderCustomElements("wishlist_name");
      this.buildWishlistDetailsPage();
      this._setupProductLinkInterception();
      this.isDetailViewActive = true;
      const activeWishlistName = this._getActiveWishlistName();
      if (activeWishlistName) {
        this._setCurrentWishlistName(activeWishlistName);
      }
      this.expectingDetailView = false;
    }

    _setEvents() {
    }

    _setupProductLinkInterception() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                const images = node.querySelectorAll ? node.querySelectorAll('.swym-wishlist-item img') : [];
                images.forEach(img => this._attachImageClickListener(img));

                if (node.matches && node.matches('img')) {
                  this._attachImageClickListener(node);
                }
              }
            });
          }
        });
      });

      const container = document.querySelector('.swym-wishlist-detail-content') ||
        document.querySelector('.swym-wishlist-container-content') ||
        document.body;

      if (container) {
        observer.observe(container, {
          childList: true,
          subtree: true
        });
      }

      const existingImages = document.querySelectorAll('.swym-wishlist-item img');
      existingImages.forEach(img => this._attachImageClickListener(img));
    }

    _attachImageClickListener(image) {
      if (image.classList.contains('product-link-intercepted')) {
        return;
      }

      const productItem = image.closest('.swym-wishlist-item');
      if (!productItem) return;

      const productLink = productItem.querySelector('a[href*="/products/"]');
      if (!productLink || !productLink.href) return;

      image.classList.add('product-link-intercepted');
      image.style.cursor = 'pointer';

      const targetUrl = productLink.href;

      image.onclick = function(e) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = targetUrl;
        return false;
      };
    }

    _setupWishlistCardInterception() {
      const cards = document.querySelectorAll('.swym-wishlist-list-card');
      cards.forEach((card) => {
        card.addEventListener('mousedown', () => {
          const wishlistName = card.querySelector('.swym-wishlist-name');
          if (wishlistName) {
            const name = wishlistName.textContent.trim();
            this._setCurrentWishlistName(name);
            this.expectingDetailView = true;
          }
        }, { once: true });
      });
    }

    removeWishlistHeader() {
      let isEmptyWishlist = !!document.querySelector(".swym-empty-wishlist-container");
      if (isEmptyWishlist) {
        return;
      }
    }

    buildWishlistDetailsPage() {
      const detailHeader = document.querySelector(".swym-wishlist-detail-header");
      if (!detailHeader) return;

      const backBtn = document.querySelector(".swym-wishlist-detail-content .swym-back-btn");
      if (backBtn) {
        const textNode = Array.from(backBtn.childNodes).find(node => node.nodeType === 3 && node.textContent.trim());
        if (textNode) {
          textNode.textContent = window.themeVariables.translations.wishlist.back;
        }
        if (!detailHeader.contains(backBtn)) {
          detailHeader.insertBefore(backBtn, detailHeader.firstChild);
        }
      }

      const actionContainer = detailHeader.querySelector('.swym-action-container');
      const isLoggedIn =  JSON.parse(localStorage.getItem('swym-authn'))?.all?.authn;
      if (isLoggedIn && actionContainer && !actionContainer.querySelector('.wishlist-delete-list-btn')) {
          const deleteBtn = this._createDeleteListButton();
          actionContainer.insertBefore(deleteBtn, actionContainer.firstChild);
        actionContainer.insertBefore(deleteBtn, actionContainer.firstChild);
      }



      const finalBackBtn = detailHeader.querySelector(".swym-back-btn");
      if (finalBackBtn && !finalBackBtn.hasAttribute('data-custom-listener')) {
        finalBackBtn.setAttribute('data-custom-listener', 'true');
        finalBackBtn.addEventListener('click', () => {
          this.isDetailViewActive = false;
          this.expectingDetailView = false;
          const customHeader = this.wishlistWidget?.querySelector('.wishlist-page-section-header--general');
          customHeader?.remove();
          this._renderCustomElements("general_header");
        });
      }
    }

    _handleDeleteCurrentList() {

      const listNameToDelete = localStorage.getItem('current_wishlist_name');
      if (!listNameToDelete) {
        console.error("Could not find the list name to delete.");
        if (window._swat && window._swat.ui) window._swat.ui.showErrorNotification({
          message: window.themeVariables.translations.wishlist.deleteListError
        });
        return;
      }

      if (!window._swat || !window._swat.wishlist || !window._swat.ui) {
        console.error("Swym API is not available.");
        if (window._swat && window._swat.ui) window._swat.ui.showErrorNotification({
          message: window.themeVariables.translations.wishlist.deleteListError
        });
        return;
      }

      const onSuccessFetch = (response) => {
        const lists = response.data || [];
        const listToDelete = lists.find(list => list.lname === listNameToDelete);

        if (listToDelete && listToDelete.lid) {
          const listId = listToDelete.lid;

          const onSuccessDelete = () => {
            const showSuccessToast = () => {
              window._swat.ui.uiRef.showSuccessNotification({
                message: window.themeVariables.translations.wishlist.deleteListSuccess
              });
            };

            if (window._swat && window._swat.ui && typeof window._swat.ui.renderWishlistPage === 'function') {
              window._swat.ui.renderWishlistPage(null, showSuccessToast);
            } else {
              showSuccessToast();
              setTimeout(() => window.location.reload(), 2000);
            }
          };

          const onErrorDelete = (error) => {
            console.error(`Error deleting list with ID ${listId}:`, error);
            window._swat.ui.uiRef.showErrorNotification(window.themeVariables.translations.wishlist.deleteListError);
          };

          window._swat.deleteList(listId, onSuccessDelete, onErrorDelete);

        } else {
          console.warn(`Could not find a list with the name "${listNameToDelete}".`);
          window._swat.ui.uiRef.showErrorNotification(window.themeVariables.translations.wishlist.deleteListError);
        }
      };

      const onErrorFetch = (error) => {
        console.error("Error fetching user lists:", error);
        window._swat.ui.uiRef.showErrorNotification({
          message: window.themeVariables.translations.wishlist.deleteListError
        });
      };

      window._swat.wishlist.fetchUserLists(onSuccessFetch, onErrorFetch);
    }

    _createDeleteListButton() {
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'wishlist-delete-list-btn swym-delete-btn swym-nav swym-nav-1 swym-is-button';
      deleteBtn.setAttribute('aria-label', window.themeVariables.translations.wishlist.deleteListButton);
      deleteBtn.innerHTML = `<span class="swym-icon"></span>${window.themeVariables.translations.wishlist.deleteListButton}`;

      deleteBtn.addEventListener('click', () => {
        this._showConfirmationModal();
      });

      return deleteBtn;
    }

    _showConfirmationModal() {

      if (document.querySelector('.wishlist-delete-modal-overlay')) {
        return;
      }

      const modalOverlay = document.createElement('div');
      modalOverlay.className = 'wishlist-delete-modal-overlay';

      const currentWishlistName = localStorage.getItem('current_wishlist_name');

      const modalContent = document.createElement('div');
      modalContent.className = 'wishlist-delete-modal-content';
      localStorage.getItem('current_wishlist_name');
      modalContent.innerHTML = `
        <h3 class="h4">${window.themeVariables.translations.wishlist.deleteListModalTitle}</h3>
        <p class="text-base">${window.themeVariables.translations.wishlist.deleteListModalSubtitle}<br><b>${currentWishlistName}</b>?</p>
        <div class="wishlist-delete-modal-actions">
          <button class="button button--outline" id="cancel-delete-btn">${window.themeVariables.translations.wishlist.deleteListModalCancelButton}</button>
          <button class="button" id="confirm-delete-btn">${window.themeVariables.translations.wishlist.deleteListModalConfirmButton} </button>
        </div>
        <button class="wishlist-delete-modal-close" aria-label="${window.themeVariables.translations.wishlist.deleteListModalCloseButton}">&times;</button>
      `;

      modalOverlay.appendChild(modalContent);
      document.body.appendChild(modalOverlay);
      document.body.style.overflow = 'hidden';

      const closeModal = () => {
        modalOverlay.remove();
        document.body.style.overflow = '';
      };

      document.getElementById('confirm-delete-btn').onclick = () => {
        this._handleDeleteCurrentList();
        closeModal();
      };

      document.getElementById('cancel-delete-btn').onclick = closeModal;
      modalOverlay.querySelector('.wishlist-delete-modal-close').onclick = closeModal;

      modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
          closeModal();
        }
      });
    }

    _renderCustomElements(step = "general_header") {
      if (step === "general_header") {
        const existingHeader = this.wishlistWidget.querySelector('.wishlist-page-section-header--general');
        if (existingHeader) existingHeader.remove();

        this.appendHtml(
          this.wishlistWidget,
          `<div class="wishlist-page-section-header wishlist-page-section-header--general">
            <h2 class="wishlist-page-section-title">${window.themeVariables.translations.wishlist.wishlistPageTitle}</h2>
            <span class="wishlist-page-section-subtitle">${window.themeVariables.translations.wishlist.wishlistPageSubtitle}</span>
          </div>`,
          "afterbegin"
        );
      }

      if (step === "wishlist_name") {
        const existingHeader = this.wishlistWidget?.querySelector(`.wishlist-page-section-header--general`);
        if (existingHeader) {
          existingHeader.remove();
        }
        const savedWishlistName = this.currentWishlistName || localStorage.getItem('current_wishlist_name');
        const wishlistTitle = savedWishlistName || window.themeVariables.translations.wishlist.wishlistPageTitle;
        this.appendHtml(
          this.wishlistWidget,
          `<div class="wishlist-page-section-header wishlist-page-section-header--general">
            <h2 class="wishlist-page-section-title">${wishlistTitle}</h2>
            <span class="wishlist-page-section-subtitle">${window.themeVariables.translations.wishlist.wishlistPageSubtitle}</span>
          </div>`,
          "afterbegin"
        );
      }
      if (step === "empty_detail") {
        const existingHeader = this.wishlistWidget?.querySelector(`.wishlist-page-section-header--general`);
        if (existingHeader) {
          existingHeader.remove();
        }
        const savedWishlistName = this.currentWishlistName || localStorage.getItem('current_wishlist_name');
        const wishlistTitle = savedWishlistName || window.themeVariables.translations.wishlist.wishlistPageTitle;
        this.appendHtml(
          this.wishlistWidget,
          `<div class="wishlist-page-section-header wishlist-page-section-header--general">
            <h2 class="wishlist-page-section-title">${wishlistTitle}</h2>
            <span class="wishlist-page-section-subtitle">${window.themeVariables.translations.wishlist.wishlistPageSubtitle}</span>
          </div>`,
          "afterbegin"
        );
      }
      if (step === "empty_list") {
        this.wishlistWidget?.querySelector(`.wishlist-page-section-header--general`)?.remove();
        if (this.wishlistWidget.querySelector(`.wishlist-page-section-header--empty`)) {
          return;
        }
        this.appendHtml(
          this.wishlistWidget,
          `<div class="wishlist-page-section-header wishlist-page-section-header--empty">
            <h2 class="wishlist-page-section-title">${window.themeVariables.translations.wishlist.wishlistPageTitle}</h2>
            <span class="wishlist-page-section-subtitle">${window.themeVariables.translations.wishlist.wishlistPageEmpty}</span>
            <span class="wishlist-page-section-actions">
              <a href="/collections/all" class="button button--outline button--xl button--inverse" data-no-instant>
                ${window.themeVariables.translations.wishlist.seeAllProducts}
              </a>
            </span>
          </div>`,
          "afterbegin"
        );
        this.wishlistWidget?.querySelector(`.swym-wishlist-detail`)?.remove();
      }
    }

    _getActiveWishlistName() {
      const dropdownName = document.querySelector('.swym-selected-wishlist-name');
      const dropdownText = dropdownName?.textContent?.trim();
      if (dropdownText) {
        return dropdownText;
      }
      const detailTitle = document.querySelector('.swym-wishlist-detail-header .swym-wishlist-main-title');
      const titleText = detailTitle?.textContent?.trim();
      return titleText || null;
    }

    _setCurrentWishlistName(name) {
      const normalized = (name || '').trim();
      if (!normalized) {
        return;
      }
      this.currentWishlistName = normalized;
      localStorage.setItem('current_wishlist_name', normalized);
    }

    _showLoadingIndicator() {
      if (!this.wishlistWidget || this.loadingIndicator) return;
      const children = Array.from(this.wishlistWidget.children);
      this.hiddenChildren = children.filter(child => !child.classList.contains('wishlist-loading')).map(child => {
        const previousDisplay = child.style.display;
        child.style.display = 'none';
        return { element: child, display: previousDisplay };
      });
      const pagePlaceholder = document.querySelector('#swym-wishlist-render-container .wishlist-loading--page');
      if (pagePlaceholder) {
        this.loadingIndicator = pagePlaceholder;
        this._animateLoader(pagePlaceholder);
        return;
      }
      const loading = document.createElement('div');
      loading.className = 'wishlist-loading';
      loading.innerHTML = '<span class="button__loader"><span></span><span></span><span></span></span>';
      this.loadingIndicator = loading;
      this.wishlistWidget.insertBefore(loading, this.wishlistWidget.firstChild);
      this._animateLoader(loading);
    }

    _hideLoadingIndicator() {
      const currentLoader = this.loadingIndicator;
      if (currentLoader) {
        this._stopLoaderAnimation(currentLoader);
        if (currentLoader.parentElement) {
          currentLoader.parentElement.removeChild(currentLoader);
        }
      }
      this.loadingIndicator = null;
      const pagePlaceholder = document.querySelector('#swym-wishlist-render-container .wishlist-loading--page');
      if (pagePlaceholder && pagePlaceholder !== currentLoader) {
        this._stopLoaderAnimation(pagePlaceholder);
        if (pagePlaceholder.parentElement) {
          pagePlaceholder.parentElement.removeChild(pagePlaceholder);
        }
      }
      if (this.hiddenChildren && this.hiddenChildren.length > 0) {
        this.hiddenChildren.forEach(item => {
          if (item.element) {
            item.element.style.display = item.display || '';
          }
        });
      }
      this.hiddenChildren = [];
    }

    _animateLoader(element) {
      if (!element) return;
      if (typeof window.animateWishlistLoader === 'function') {
        window.animateWishlistLoader(element);
        return;
      }
      const loader = element.querySelector('.button__loader');
      if (!loader) return;
      const dots = loader.querySelectorAll('span');
      dots.forEach((dot, index) => {
        if (dot._wishlistLoaderAnimation || typeof dot.animate !== 'function') return;
        dot._wishlistLoaderAnimation = dot.animate(
          { opacity: [1, 0.1] },
          {
            duration: 350,
            delay: index * (350 / 3),
            direction: 'alternate',
            iterations: Infinity,
            easing: 'ease-in-out'
          }
        );
      });
    }

    _stopLoaderAnimation(element) {
      if (!element) return;
      const loader = element.querySelector('.button__loader');
      if (!loader) return;
      const dots = loader.querySelectorAll('span');
      dots.forEach(dot => {
        if (dot._wishlistLoaderAnimation) {
          dot._wishlistLoaderAnimation.cancel?.();
          dot._wishlistLoaderAnimation = null;
        }
      });
    }
  }

  return new WishlistWidget(container);
}


